'use client';

import { c as _c } from "react/compiler-runtime";
import type { DetailDeliveryAddressResponse } from '__generated__/api/auth';
import { SvgPencil } from '@common/icon';
import { format } from '@core/utils';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { AuthInfiniteQueryOptions } from '@/api';
import { DeliveryAddressEditModal } from '@/app/donation/(private)/cart/order/_components/DeliveryAddressEditModal';
import { PageNavigation } from '@/components/client';
const PAGE_SIZE = 10;
const DeliveryAddressPage = () => {
  const $ = _c(34);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const loadMoreRef = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      ...AuthInfiniteQueryOptions["get:/wello/v2/auth/home/delivery"]({
        query: {
          page: 1,
          size: PAGE_SIZE
        }
      }),
      select: _temp2,
      getNextPageParam: _temp3,
      initialPageParam: 1
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    data: t1,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch
  } = useInfiniteQuery(t0);
  let t2;
  if ($[1] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const deliveryAddresses = t2;
  let t3;
  let t4;
  if ($[3] !== fetchNextPage || $[4] !== hasNextPage || $[5] !== isFetchingNextPage) {
    t3 = () => {
      if (!loadMoreRef.current || !hasNextPage || isFetchingNextPage) {
        return;
      }
      const observer = new IntersectionObserver(entries => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      }, {
        threshold: 0.1
      });
      observer.observe(loadMoreRef.current);
      return () => observer.disconnect();
    };
    t4 = [hasNextPage, isFetchingNextPage, fetchNextPage];
    $[3] = fetchNextPage;
    $[4] = hasNextPage;
    $[5] = isFetchingNextPage;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = addr => {
      setEditingAddress(addr);
      setEditModalOpen(true);
    };
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const handleEditAddress = t5;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setEditingAddress(null);
      setEditModalOpen(true);
    };
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const handleNewAddress = t6;
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setEditModalOpen(false);
      setEditingAddress(null);
    };
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const handleCloseEditModal = t7;
  let t8;
  if ($[11] !== refetch) {
    t8 = () => {
      refetch();
    };
    $[11] = refetch;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const handleEditSuccess = t8;
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <PageNavigation title={"\uBC30\uC1A1\uC9C0 \uAD00\uB9AC"} hideHomeButton={true} />;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== deliveryAddresses.length) {
    t10 = deliveryAddresses.length === 0 && <div className="flex flex-col items-center justify-center gap-8 py-80"><p className="font-500 text-Gray-600 typo-body3">등록된 배송지가 없어요.</p><p className="font-400 text-Gray-500 typo-body4">신규 배송지를 추가해주세요.</p></div>;
    $[14] = deliveryAddresses.length;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== deliveryAddresses) {
    let t12;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = addr_0 => <div key={addr_0.addressId} className="flex flex-col gap-12 rounded-medium border border-Gray-300 bg-Gray-white p-24"><div className="flex flex-col gap-4"><div className="flex items-center justify-between"><div className="flex items-center gap-8"><span className="font-700 text-Gray-900 typo-body4">{addr_0.recipientName}</span>{addr_0.primaryYN && <span className="rounded-xsmall bg-Gray-600 px-6 py-2 font-600 text-Gray-white typo-caption2">기본 배송지</span>}</div><button type="button" onClick={() => handleEditAddress(addr_0)} aria-label={"\uC218\uC815"}><SvgPencil className="size-16 text-Gray-700" /></button></div><p className="font-400 text-Gray-800 typo-body4">{addr_0.address} {addr_0.addressDetail}</p><p className="font-400 text-Gray-800 typo-body4">{format.phone(addr_0.recipientNo)}</p>{addr_0.entrancePasscode && <p className="font-400 text-Gray-550 typo-caption1">{addr_0.entrancePasscode}</p>}</div></div>;
      $[18] = t12;
    } else {
      t12 = $[18];
    }
    t11 = deliveryAddresses.map(t12);
    $[16] = deliveryAddresses;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div ref={loadMoreRef} className="h-1" />;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== isFetchingNextPage) {
    t13 = isFetchingNextPage && <div className="flex justify-center py-8"><div className="size-24 animate-spin rounded-pill border-2 border-Gray-300 border-t-Gray-900" /></div>;
    $[20] = isFetchingNextPage;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <button type="button" onClick={handleNewAddress} className="flex w-full items-center justify-center gap-8 rounded-medium border border-Gray-300 py-16 font-500 text-Gray-700 typo-body3"><span>+</span><span>신규 배송지 추가</span></button>;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== t10 || $[24] !== t11 || $[25] !== t13) {
    t15 = <div className="flex min-h-screen flex-col bg-Gray-white">{t9}<div className="flex flex-1 flex-col gap-12 px-20 py-16">{t10}{t11}{t12}{t13}{t14}</div></div>;
    $[23] = t10;
    $[24] = t11;
    $[25] = t13;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== editModalOpen || $[28] !== editingAddress || $[29] !== handleEditSuccess) {
    t16 = <DeliveryAddressEditModal opened={editModalOpen} onClose={handleCloseEditModal} address={editingAddress} onSuccess={handleEditSuccess} />;
    $[27] = editModalOpen;
    $[28] = editingAddress;
    $[29] = handleEditSuccess;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== t15 || $[32] !== t16) {
    t17 = <>{t15}{t16}</>;
    $[31] = t15;
    $[32] = t16;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  return t17;
};
export default DeliveryAddressPage;
function _temp(t0) {
  const {
    context
  } = t0;
  return context?.contents ?? [];
}
function _temp2(t0) {
  const {
    pages
  } = t0;
  return pages.flatMap(_temp);
}
function _temp3(lastPage) {
  if (lastPage.context?.totalCount && lastPage.context.page) {
    const nextPage = lastPage.context.page + 1;
    const maxPage = Math.ceil(lastPage.context.totalCount / PAGE_SIZE);
    return nextPage <= maxPage ? nextPage : undefined;
  }
}